import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import cohortService from "./cohortService";

const initialState = {
  hasAccess: false,
  accessChecked: false, // Track if access verification has been attempted
  cohortNumber: null,
  curriculum: [],
  currentChallenge: null,
  testResults: null,
  debugLogs: null,
  quizResults: null,
  isSubmitting: false,
  isLoading: false,
  leaderboard: [],
  myProgress: { totalPoints: 0, completedChallenges: 0, rank: null },
  error: null,
  sessionExpired: false,
};

export const verifyCohortAccess = createAsyncThunk(
  "cohort/verifyAccess",
  async (_, thunkAPI) => {
    try {
      const result = await cohortService.verifyAccess();
      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCurriculum = createAsyncThunk(
  "cohort/fetchCurriculum",
  async (_, thunkAPI) => {
    try {
      return await cohortService.getCurriculum();
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchChallenge = createAsyncThunk(
  "cohort/fetchChallenge",
  async (challengeId, thunkAPI) => {
    try {
      return await cohortService.getChallenge(challengeId);
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitChallenge = createAsyncThunk(
  "cohort/submitChallenge",
  async ({ challengeId, apexBody }, thunkAPI) => {
    try {
      return await cohortService.submitChallenge({ challengeId, apexBody });
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markVideoComplete = createAsyncThunk(
  "cohort/markVideoComplete",
  async (challengeId, thunkAPI) => {
    try {
      return await cohortService.markVideoComplete(challengeId);
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitCohortQuiz = createAsyncThunk(
  "cohort/submitCohortQuiz",
  async ({ challengeId, answers }, thunkAPI) => {
    try {
      return await cohortService.submitCohortQuiz({ challengeId, answers });
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLeaderboard = createAsyncThunk(
  "cohort/fetchLeaderboard",
  async (_, thunkAPI) => {
    try {
      return await cohortService.getLeaderboard();
    } catch (error) {
      if (error.response?.status === 401) {
        return thunkAPI.rejectWithValue({ sessionExpired: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cohortSlice = createSlice({
  name: "cohort",
  initialState,
  reducers: {
    clearTestResults: (state) => {
      state.testResults = null;
      state.debugLogs = null;
    },
    clearQuizResults: (state) => {
      state.quizResults = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSessionExpired: (state) => {
      state.sessionExpired = false;
    },
    updateChallengeStatus: (state, action) => {
      const { challengeId, status } = action.payload;
      // Update the challenge status in curriculum
      state.curriculum = state.curriculum.map((week) => ({
        ...week,
        items: week.items.map((item) =>
          item.id === challengeId ? { ...item, status } : item
        ),
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      // Verify Access
      .addCase(verifyCohortAccess.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyCohortAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessChecked = true;
        state.hasAccess = action.payload.hasAccess;
        state.cohortNumber = action.payload.cohortNumber || null;
      })
      .addCase(verifyCohortAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.accessChecked = true;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Fetch Curriculum
      .addCase(fetchCurriculum.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurriculum.fulfilled, (state, action) => {
        state.isLoading = false;
        state.curriculum = action.payload.curriculum;
      })
      .addCase(fetchCurriculum.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Fetch Challenge
      .addCase(fetchChallenge.pending, (state) => {
        state.isLoading = true;
        state.testResults = null;
        state.debugLogs = null;
      })
      .addCase(fetchChallenge.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentChallenge = action.payload;
      })
      .addCase(fetchChallenge.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Submit Challenge
      .addCase(submitChallenge.pending, (state) => {
        state.isSubmitting = true;
        state.testResults = null;
        state.debugLogs = null;
      })
      .addCase(submitChallenge.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.testResults = action.payload.data;
        state.debugLogs = action.payload.debugLogs;
        // Update current challenge progress
        if (state.currentChallenge) {
          state.currentChallenge.progress = action.payload.progress;
        }
        // Update curriculum if challenge completed
        if (action.payload.progress?.status === "Completed") {
          const challengeId = state.currentChallenge?.challenge?.id;
          if (challengeId) {
            state.curriculum = state.curriculum.map((week) => ({
              ...week,
              items: week.items.map((item, index, arr) => {
                if (item.id === challengeId) {
                  return { ...item, status: "Completed" };
                }
                // Unlock next challenge in same week
                if (index > 0 && arr[index - 1].id === challengeId) {
                  return { ...item, unlocked: true };
                }
                return item;
              }),
            }));
          }
        }
      })
      .addCase(submitChallenge.rejected, (state, action) => {
        state.isSubmitting = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Mark Video Complete
      .addCase(markVideoComplete.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(markVideoComplete.fulfilled, (state, action) => {
        state.isSubmitting = false;
        // Update current challenge progress
        if (state.currentChallenge) {
          state.currentChallenge.progress = { status: "Completed" };
        }
        // Update curriculum
        const challengeId = state.currentChallenge?.challenge?.id;
        if (challengeId) {
          state.curriculum = state.curriculum.map((week) => ({
            ...week,
            items: week.items.map((item, index, arr) => {
              if (item.id === challengeId) {
                return { ...item, status: "Completed" };
              }
              // Unlock next challenge in same week
              if (index > 0 && arr[index - 1].id === challengeId) {
                return { ...item, unlocked: true };
              }
              return item;
            }),
          }));
        }
      })
      .addCase(markVideoComplete.rejected, (state, action) => {
        state.isSubmitting = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Submit Cohort Quiz
      .addCase(submitCohortQuiz.pending, (state) => {
        state.isSubmitting = true;
        state.quizResults = null;
      })
      .addCase(submitCohortQuiz.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.quizResults = action.payload.data;
        // Update current challenge progress
        if (state.currentChallenge) {
          state.currentChallenge.progress = action.payload.progress;
        }
        // Update curriculum if quiz completed
        if (action.payload.progress?.status === "Completed") {
          const challengeId = state.currentChallenge?.challenge?.id;
          if (challengeId) {
            state.curriculum = state.curriculum.map((week) => ({
              ...week,
              items: week.items.map((item, index, arr) => {
                if (item.id === challengeId) {
                  return { ...item, status: "Completed" };
                }
                // Unlock next challenge in same week
                if (index > 0 && arr[index - 1].id === challengeId) {
                  return { ...item, unlocked: true };
                }
                return item;
              }),
            }));
          }
        }
      })
      .addCase(submitCohortQuiz.rejected, (state, action) => {
        state.isSubmitting = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      })
      // Fetch Leaderboard
      .addCase(fetchLeaderboard.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaderboard = action.payload.leaderboard;
        state.myProgress = action.payload.myProgress;
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload?.sessionExpired) {
          state.sessionExpired = true;
        }
        state.error = action.payload;
      });
  },
});

export const { clearTestResults, clearQuizResults, clearError, clearSessionExpired, updateChallengeStatus } =
  cohortSlice.actions;

export default cohortSlice.reducer;
